import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { TaskSummaryTotalResponse } from '@/api/generated/models/TaskSummaryTotalResponse';
import type { TaskSummaryMonthlyResponse } from '@/api/generated/models/TaskSummaryMonthlyResponse';
import { handleAsyncThunkError } from './errorUtils';
import { TaskSummaryService } from '@/api'

// Task summary filters interface
export interface TaskSummaryFilters {
  workspaceId: string;
  departmentId?: string | null;
  businessId?: string | null;
  assigneeId?: string | null;
  stakeholderId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

// Task summary state interface
interface TaskSummaryState {
  summary: TaskSummaryTotalResponse | null;
  monthSummary: TaskSummaryMonthlyResponse | null;
  isLoading: boolean;
  error: string | null;
  lastFetchFilters: TaskSummaryFilters | null;
}

// Initial state
const initialState: TaskSummaryState = {
  summary: null,
  monthSummary: null,
  isLoading: false,
  error: null,
  lastFetchFilters: null,
};

// Async thunks
export const fetchTaskSummary = createAsyncThunk(
  'taskSummary/fetchTaskSummary',
  async (filters: TaskSummaryFilters, { rejectWithValue }) => {
    try {
      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryTotal({
        workspaceId: filters.workspaceId,
        departmentId: filters.departmentId || undefined,
        businessId: filters.businessId || undefined,
        assigneeId: filters.assigneeId || undefined,
        stakeholderId: filters.stakeholderId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      return { summary: response, filters };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '업무 요약 정보 조회에 실패했습니다.');
    }
  }
);

export const fetchTaskSummaryMonth = createAsyncThunk(
  'taskSummary/fetchTaskSummaryMonth',
  async (filters: TaskSummaryFilters, { rejectWithValue }) => {
    try {
      const response = await TaskSummaryService.appsTaskViewsGetTaskSummaryMonthly({
        workspaceId: filters.workspaceId,
        departmentId: filters.departmentId || undefined,
        businessId: filters.businessId || undefined,
        assigneeId: filters.assigneeId || undefined,
        stakeholderId: filters.stakeholderId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      return { monthSummary: response, filters };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '월별 업무 요약 정보 조회에 실패했습니다.');
    }
  }
);

// Task summary slice
const taskSummarySlice = createSlice({
  name: 'taskSummary',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSummary: (state) => {
      state.summary = null;
      state.monthSummary = null;
      state.lastFetchFilters = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch task summary
      .addCase(fetchTaskSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.summary = action.payload.summary;
        state.lastFetchFilters = action.payload.filters;
        state.error = null;
      })
      .addCase(fetchTaskSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch task summary month
      .addCase(fetchTaskSummaryMonth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskSummaryMonth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.monthSummary = action.payload.monthSummary;
        state.lastFetchFilters = action.payload.filters;
        state.error = null;
      })
      .addCase(fetchTaskSummaryMonth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSummary } = taskSummarySlice.actions;
export default taskSummarySlice.reducer;